import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface Inquiry {
  id: string;
  title: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  product_info: string;
  message: string;
  password: string;
  created_at: string;
  attachment_url: string | null;
  attachment_name: string | null;
  reply_content: string | null;
  reply_date: string | null;
  status: string | null;
}

interface InquiryContextType {
  inquiries: Inquiry[];
  addInquiry: (inquiry: Omit<Inquiry, 'id' | 'created_at' | 'reply_content' | 'reply_date' | 'status'>) => Promise<void>;
  deleteInquiry: (id: string) => Promise<void>;
  updateInquiryReply: (id: string, replyContent: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  refreshInquiries: () => Promise<void>;
}

const InquiryContext = createContext<InquiryContextType | undefined>(undefined);

export const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error('useInquiry must be used within InquiryProvider');
  }
  return context;
};

export const useInquiries = useInquiry;

interface InquiryProviderProps {
  children: ReactNode;
  initialData?: any[];
}

export const InquiryProvider = ({ children }: InquiryProviderProps) => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchInquiries = async (isRetry = false) => {
    try {
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        // PGRST002 에러는 재시도
        if (fetchError.code === 'PGRST002' && retryCount < 3) {
          console.log(`Retrying... (${retryCount + 1}/3)`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchInquiries(true), 2000);
          return;
        }
        throw fetchError;
      }
      
      setInquiries(data || []);
      setRetryCount(0);
    } catch (err: any) {
      console.error('Error fetching inquiries:', err);
      setError(err.message || '문의 목록을 불러오는데 실패했습니다.');
    } finally {
      if (!isRetry) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchInquiries();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('inquiries_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inquiries' }, () => {
        fetchInquiries();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const addInquiry = async (inquiry: Omit<Inquiry, 'id' | 'created_at' | 'reply_content' | 'reply_date' | 'status'>) => {
    try {
      setError(null);
      const { error: insertError } = await supabase.from('inquiries').insert([inquiry]);
      if (insertError) throw insertError;
      await fetchInquiries();
    } catch (err: any) {
      console.error('Error adding inquiry:', err);
      setError(err.message || '문의 등록에 실패했습니다.');
      throw err;
    }
  };

  const deleteInquiry = async (id: string) => {
    try {
      setError(null);
      const { error: deleteError } = await supabase.from('inquiries').delete().eq('id', id);
      if (deleteError) throw deleteError;
      await fetchInquiries();
    } catch (err: any) {
      console.error('Error deleting inquiry:', err);
      setError(err.message || '문의 삭제에 실패했습니다.');
      throw err;
    }
  };

  const updateInquiryReply = async (id: string, replyContent: string) => {
    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('inquiries')
        .update({
          reply_content: replyContent,
          reply_date: new Date().toISOString(),
          status: '답변완료'
        })
        .eq('id', id);
      if (updateError) throw updateError;
      await fetchInquiries();
    } catch (err: any) {
      console.error('Error updating reply:', err);
      setError(err.message || '답변 등록에 실패했습니다.');
      throw err;
    }
  };

  const refreshInquiries = async () => {
    setLoading(true);
    await fetchInquiries();
  };

  return (
    <InquiryContext.Provider value={{ inquiries, addInquiry, deleteInquiry, updateInquiryReply, loading, error, refreshInquiries }}>
      {children}
    </InquiryContext.Provider>
  );
};
